import { useCallback, useMemo, useState, type FC } from 'react';
import { Button, Tag } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import {
  ColorByAttributeModal,
  type ColorByApplyTarget,
  type ColorByCategory,
  type ColorByOption,
  type ColorByRule,
} from 'components/color-by-attribute-modal';
import { ColorRulesLegend } from 'pages/map-query/color-by-section';
import { WELL_COLOR_TARGETS, newRuleApplyTo, targetTakenBy } from './targets';
import styles from './wells-color-by-section.module.scss';

interface Props {
  /** The attributes this host can colour by. Differs per host — see the callers. */
  categories: ColorByCategory[];
  /** The values of one attribute, as the host can source them. */
  getOptions: (attributeId: string) => ColorByOption[];
  rules: ColorByRule[];
  onChange: (rules: ColorByRule[]) => void;
  /**
   * The surfaces this host can paint. Defaults to the Well page's four; the workspace
   * passes its five, which add the well list.
   */
  targets?: ColorByApplyTarget[];
}

/**
 * The rail's "Color by Attribute" control and the rules it has produced.
 *
 * Shared by the Well page and the Well Workspace, which mock v13 gives the same section;
 * only the attributes and their value source differ, so those are props. The rule
 * builder is map-query's, given one untabbed attribute list.
 * @param {Props} props The attributes, the value source, and the rule stack.
 * @return {JSX.Element} The button, the legend and the modal.
 */
export const WellsColorBySection: FC<Props> = ({
  categories,
  getOptions,
  rules,
  onChange,
  targets = WELL_COLOR_TARGETS,
}) => {
  const [open, setOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  const openModal = (ruleId: string | null) => {
    setEditingRuleId(ruleId);
    setOpen(true);
  };

  // What a fresh rule paints, and what Reset returns it to: everything this host has.
  const defaultApplyTo = useMemo(() => newRuleApplyTo(targets), [targets]);

  /**
   * Copies a rule so its values and colours can be retargeted without rebuilding it,
   * which matters because a surface can only be claimed once. The copy takes the free
   * surfaces, and arrives disabled when none are free rather than painting nothing.
   */
  const duplicateRule = useCallback(
    (ruleId: string) => {
      const source = rules.find((rule) => rule.ruleId === ruleId);
      if (!source) return;

      const taken = targetTakenBy(rules, null);
      const free = targets.filter(
        (target) => !taken(source.attribute, target.id)
      );

      const copy: ColorByRule = {
        ...source,
        ruleId: `${source.attribute}-${Date.now()}-${Math.round(
          Math.random() * 1000
        )}`,
        applyTo: Object.fromEntries(
          targets.map((target) => [
            target.id,
            free.some((one) => one.id === target.id),
          ])
        ),
        enabled: free.length > 0,
      };

      // Directly after the rule it came from, so the pair reads together.
      const at = rules.findIndex((rule) => rule.ruleId === ruleId);
      onChange([...rules.slice(0, at + 1), copy, ...rules.slice(at + 1)]);
    },
    [rules, targets, onChange]
  );

  const updateRule = useCallback(
    (ruleId: string, change: (rule: ColorByRule) => ColorByRule) =>
      onChange(
        rules.map((rule) => (rule.ruleId === ruleId ? change(rule) : rule))
      ),
    [rules, onChange]
  );

  // The legend names a rule by its attribute, and its own map only covers Map Query's
  // facets — without these, colouring by anything else shows the raw id. Phrased
  // "Colored by X" because that is how mock v13 reads on both rails.
  const attributeLabels = useMemo(
    () =>
      Object.fromEntries(
        categories.map((category) => [
          category.id,
          `Colored by ${category.label}`,
        ])
      ),
    [categories]
  );

  return (
    <div className={styles.section}>
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Add}
        onClick={() => openModal(null)}
      >
        Color by Attribute
      </Button>

      {rules.length === 0 ? (
        <p className={styles.empty}>No color rules added.</p>
      ) : (
        <ColorRulesLegend
          rules={rules}
          onEdit={openModal}
          onRemove={(ruleId) =>
            onChange(rules.filter((rule) => rule.ruleId !== ruleId))
          }
          onToggleEnabled={(ruleId) =>
            updateRule(ruleId, (rule) => ({ ...rule, enabled: !rule.enabled }))
          }
          onColorChange={(ruleId, value, color) =>
            updateRule(ruleId, (rule) => ({
              ...rule,
              valueColors: { ...rule.valueColors, [value]: color },
            }))
          }
          onReorder={onChange}
          attributeLabels={attributeLabels}
          renderRuleMeta={(rule) => <ActiveOn rule={rule} targets={targets} />}
          onDuplicate={duplicateRule}
          actionsLayout="stacked"
        />
      )}

      {/* Mounted only while open. Carbon keeps a closed modal's content in the DOM, so
          left mounted the value list — every well status, every county — sits in the
          page's text the whole time: found by a text query, read by a screen reader,
          and built on every render for a panel nobody has opened. */}
      {open && (
        <ColorByAttributeModal
          open
          onClose={() => setOpen(false)}
          editingRuleId={editingRuleId}
          rules={rules}
          onApply={onChange}
          categories={categories}
          getOptions={getOptions}
          applyToTargets={targets}
          newRuleApplyTo={defaultApplyTo}
          // Reset means "back to a fresh rule", so it restores the surfaces a new rule
          // starts with. Left unset it defaults to none, and since nothing stops Apply
          // with an empty scope that produced a rule which painted nothing.
          resetApplyTo={defaultApplyTo}
          isTargetDisabled={targetTakenBy(rules, editingRuleId)}
          // Both opt-in, so the Cross-Section, Pre-Drill and Area rails that share this
          // modal keep the behaviour they shipped with.
          showPasteList
          requireApplyTarget
          loadingDescription="Loading wells…"
        />
      )}
    </div>
  );
};

/**
 * The mock's "Active on: Map, 3D View" line, under a rule's name.
 *
 * Without it a reader cannot tell which view a rule paints, which matters because two
 * rules cannot claim the same surface.
 * @param {{ rule: ColorByRule, targets: ColorByApplyTarget[] }} props The rule and the
 *   host's surfaces, read from the host so the workspace's fifth is included.
 * @return {JSX.Element | null} The line, or nothing when the rule paints nowhere.
 */
const ActiveOn: FC<{ rule: ColorByRule; targets: ColorByApplyTarget[] }> = ({
  rule,
  targets,
}) => {
  const active = targets.filter((target) => rule.applyTo[target.id]);

  if (active.length === 0) return null;

  return (
    <p className={styles.activeOn}>
      <span className={styles.activeOnLabel}>Active on:</span>
      {/* Tags rather than a comma list: with five possible surfaces the sentence ran
          long enough to wrap the rail, and each surface is a discrete thing rather
          than prose. Mock v13 chips them for the same reason. */}
      {active.map((target) => (
        <Tag key={target.id} size="sm" type="gray">
          {target.label}
        </Tag>
      ))}
    </p>
  );
};
